import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useAuth, hasRole } from '../contexts/AuthContext';
import {
  Truck, Users, FileText, ClipboardCheck, Send, Wrench, FileSignature, AlertTriangle,
  CheckCircle2, Clock, Plus, ArrowRight, TrendingUp, Layers, ShieldCheck, Activity,
  Calendar, FileX, AlertCircle, Eye,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '../components/StatusBadge';
import { PageHeader } from '../components/PageHeader';
import { ScrollArea } from '@/components/ui/scroll-area';
import { STATUS_LABELS, STATUS_COLORS, formatDate, formatDateTime, ROLE_LABELS } from '../lib/format';

const StatCard = ({ icon: Icon, label, value, accent = 'blue', onClick, alert }) => {
  const colors = {
    blue: 'bg-blue-50 text-[hsl(217,91%,24%)]',
    amber: 'bg-amber-50 text-amber-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    rose: 'bg-rose-50 text-rose-700',
    violet: 'bg-violet-50 text-violet-700',
    sky: 'bg-sky-50 text-sky-700',
    slate: 'bg-slate-100 text-slate-700',
    red: 'bg-red-50 text-red-700',
  };
  return (
    <button onClick={onClick} className={`stat-card text-left w-full group relative ${alert ? 'ring-1 ring-red-200' : ''}`}>
      {alert && <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-red-500 animate-pulse" />}
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm text-slate-500">{label}</div>
          <div className="text-3xl font-bold text-slate-900 mt-1">{value ?? 0}</div>
        </div>
        <div className={`h-11 w-11 rounded-xl grid place-items-center ${colors[accent]}`}><Icon className="h-5 w-5" /></div>
      </div>
      <div className="mt-3 flex items-center text-xs text-slate-500 group-hover:text-[hsl(217,91%,24%)]">
        View details <ArrowRight className="h-3 w-3 ml-1" />
      </div>
    </button>
  );
};

const ACTION_LABELS = {
  create_vehicle: 'created equipment',
  update_vehicle: 'updated equipment',
  delete_vehicle: 'deleted equipment',
  upload_vehicles: 'bulk-uploaded equipment',
  batch_delete_vehicles: 'batch-deleted equipment',
  create_operator: 'added operator',
  update_operator: 'updated operator',
  create_request: 'submitted equipment request',
  approve_request: 'approved request',
  reject_request: 'rejected request',
  update_inspection: 'updated inspection',
  approve_inspection: 'approved inspection',
  reject_inspection: 'rejected inspection',
  update_mobilization: 'updated mobilization',
  create_maintenance: 'logged maintenance',
  update_maintenance: 'updated maintenance',
  create_queue: 'sent to equipment queue',
  assign_queue: 'assigned alternative equipment',
  cancel_queue: 'cancelled queue entry',
  create_user: 'created user account',
  update_user: 'updated user',
  delete_user: 'deleted user',
  reset_password: 'reset password',
  create_role: 'created custom role',
  update_role: 'updated role permissions',
  delete_role: 'deleted role',
  update_approval_workflow: 'updated approval workflow',
  sync_dropdowns: 'synced dropdown data',
  backup_data: 'created backup',
  restore_data: 'restored backup',
  purge_data: 'purged data',
  purge_failed: 'attempted purge (failed)',
  create_contract: 'created contract',
  create_billing: 'issued invoice',
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const load = () => api.get('/dashboard/stats').then((r) => setStats(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30000); // auto-refresh every 30s for transparency
    return () => clearInterval(t);
  }, []);

  if (!stats) return <div className="text-slate-500">Loading dashboard...</div>;

  const fleetTotal = Object.values(stats.fleet_status || {}).reduce((a, b) => a + b, 0);
  const operationalPct = fleetTotal ? Math.round(((stats.fleet_status?.operational || 0) / fleetTotal) * 100) : 0;
  const isSuperAdmin = hasRole(user, 'super_admin');

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        description="Live, transparent overview of fleet operations, workflow status, alerts, and recent activity. Auto-refreshes every 30 seconds."
        actions={
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-[10px]"><Activity className="h-3 w-3 mr-1 text-emerald-600" /> Live</Badge>
            {hasRole(user, 'super_admin', 'admin', 'requestor') && (
              <Button onClick={() => navigate('/esd/equipment-requests?new=1')} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]">
                <Plus className="h-4 w-4 mr-1.5" /> New Equipment Request
              </Button>
            )}
          </div>
        }
      />

      {/* Personal section: Needs your attention */}
      {(stats.my_pending_approvals?.length > 0 || stats.in_queue > 0 || stats.pending_inspections > 0) && hasRole(user, 'super_admin', 'admin', 'approver') && (
        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
          <CardContent className="p-4 lg:p-5">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-100 text-amber-700 grid place-items-center shrink-0">
                <AlertCircle className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-semibold text-slate-900">Needs your attention</h3>
                  <span className="text-xs text-slate-500">{ROLE_LABELS[user?.role]} actions</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                  {stats.pending_requests > 0 && <button onClick={() => navigate('/esd/equipment-requests?tab=pending')} className="px-3 py-1.5 rounded-md bg-white border border-amber-200 hover:border-amber-400 transition-colors"><span className="font-semibold text-amber-800">{stats.pending_requests}</span> requests pending approval</button>}
                  {stats.pending_inspections > 0 && <button onClick={() => navigate('/esd/inspections')} className="px-3 py-1.5 rounded-md bg-white border border-sky-200 hover:border-sky-400 transition-colors"><span className="font-semibold text-sky-800">{stats.pending_inspections}</span> inspections to review</button>}
                  {stats.in_queue > 0 && <button onClick={() => navigate('/esd/equipment-queues')} className="px-3 py-1.5 rounded-md bg-white border border-amber-300 hover:border-amber-500 transition-colors"><span className="font-semibold text-amber-800">{stats.in_queue}</span> equipment in queue</button>}
                  {stats.overdue_maintenance > 0 && <button onClick={() => navigate('/esd/maintenance')} className="px-3 py-1.5 rounded-md bg-white border border-red-200 hover:border-red-400 transition-colors"><span className="font-semibold text-red-800">{stats.overdue_maintenance}</span> overdue maintenance</button>}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats grid */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Fleet & Workflow at a glance</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Truck} label="Total Equipment" value={stats.total_vehicles} accent="blue" onClick={() => navigate('/esd/equipment')} />
          <StatCard icon={Users} label="Active Operators" value={stats.total_operators} accent="emerald" onClick={() => navigate('/esd/operators')} />
          <StatCard icon={FileText} label="Pending Requests" value={stats.pending_requests} accent="amber" onClick={() => navigate('/esd/equipment-requests')} alert={stats.pending_requests > 0} />
          <StatCard icon={ClipboardCheck} label="Pending Inspections" value={stats.pending_inspections} accent="sky" onClick={() => navigate('/esd/inspections')} alert={stats.pending_inspections > 0} />
          <StatCard icon={Layers} label="In Equipment Queue" value={stats.in_queue} accent="rose" onClick={() => navigate('/esd/equipment-queues')} alert={stats.in_queue > 0} />
          <StatCard icon={Send} label="Active Mobilizations" value={stats.active_mobilizations} accent="violet" onClick={() => navigate('/esd/mobilizations')} />
          <StatCard icon={Wrench} label="Open Maintenance" value={stats.open_maintenance} accent="rose" onClick={() => navigate('/esd/maintenance')} />
          <StatCard icon={FileSignature} label="Active Contracts" value={stats.active_contracts} accent="slate" onClick={() => navigate('/esd/contracts')} />
        </div>
      </div>

      {/* Operational health & expiring documents */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between">
              <span className="flex items-center gap-2"><Truck className="h-5 w-5" /> Fleet Status</span>
              <span className="text-sm font-normal text-slate-500">
                <span className="text-emerald-600 font-semibold">{operationalPct}%</span> operational
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(stats.fleet_status || {}).map(([s, count]) => (
                <button key={s} onClick={() => navigate(`/esd/equipment?status=${s}`)} className="text-left rounded-lg border border-slate-200 p-3 hover:border-[hsl(217,91%,24%)] hover:shadow-sm transition">
                  <div className="flex items-center justify-between">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${STATUS_COLORS[s] || 'bg-slate-100 border-slate-200'}`}>{STATUS_LABELS[s] || s}</span>
                    <span className="text-xs text-slate-400">{fleetTotal ? Math.round((count / fleetTotal) * 100) : 0}%</span>
                  </div>
                  <div className="text-2xl font-bold mt-2 text-slate-900">{count}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className={(stats.overdue_maintenance > 0 || stats.due_soon_maintenance > 0) ? 'border-amber-200' : ''}>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Wrench className="h-5 w-5" /> Maintenance Health</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <button onClick={() => navigate('/esd/maintenance')} className="w-full flex items-center justify-between p-3 rounded-md border border-red-200 bg-red-50/50 hover:bg-red-50">
              <div className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-600" /><span className="text-sm font-medium">Overdue</span></div>
              <span className="text-2xl font-bold text-red-700">{stats.overdue_maintenance}</span>
            </button>
            <button onClick={() => navigate('/esd/maintenance')} className="w-full flex items-center justify-between p-3 rounded-md border border-amber-200 bg-amber-50/50 hover:bg-amber-50">
              <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-amber-600" /><span className="text-sm font-medium">Due in 30 days</span></div>
              <span className="text-2xl font-bold text-amber-700">{stats.due_soon_maintenance}</span>
            </button>
            <button onClick={() => navigate('/esd/maintenance')} className="w-full flex items-center justify-between p-3 rounded-md border border-sky-200 bg-sky-50/50 hover:bg-sky-50">
              <div className="flex items-center gap-2"><Wrench className="h-4 w-4 text-sky-600" /><span className="text-sm font-medium">Open work orders</span></div>
              <span className="text-2xl font-bold text-sky-700">{stats.open_maintenance}</span>
            </button>
          </CardContent>
        </Card>
      </div>

      {/* Expiring documents & licenses */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className={stats.expiring_docs?.length > 0 ? 'border-amber-200' : ''}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><FileX className="h-5 w-5 text-amber-700" /> Expiring Equipment Documents</CardTitle>
            <Badge variant="secondary">{stats.expiring_docs?.length || 0}</Badge>
          </CardHeader>
          <CardContent>
            {stats.expiring_docs?.length === 0 ? (
              <div className="text-sm text-slate-500 py-4 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> All documents are valid for the next 30 days.</div>
            ) : (
              <ScrollArea className="max-h-56">
                <table className="w-full text-xs">
                  <thead className="text-left text-slate-500 border-b sticky top-0 bg-white">
                    <tr><th className="py-2 pr-2">Equipment</th><th className="py-2 pr-2">Doc</th><th className="py-2 pr-2">Expires</th></tr>
                  </thead>
                  <tbody className="divide-y">
                    {(stats.expiring_docs || []).slice(0, 20).map((d) => (
                      <tr key={`doc-${d.equipment_code}-${d.type}-${d.expires}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate('/esd/equipment')}>
                        <td className="py-1.5 pr-2"><div className="font-mono font-semibold">{d.equipment_code}</div><div className="text-[10px] text-slate-500 truncate max-w-xs">{d.description}</div></td>
                        <td className="py-1.5 pr-2"><Badge variant="secondary" className="text-[10px]">{d.type}</Badge></td>
                        <td className="py-1.5 pr-2">
                          {d.expired ? <Badge className="bg-red-100 text-red-800 hover:bg-red-100 text-[10px]">Expired {formatDate(d.expires)}</Badge> : <span className="text-amber-700">{formatDate(d.expires)}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        <Card className={stats.expiring_licenses?.length > 0 ? 'border-amber-200' : ''}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-amber-700" /> Expiring Operator Licenses</CardTitle>
            <Badge variant="secondary">{stats.expiring_licenses?.length || 0}</Badge>
          </CardHeader>
          <CardContent>
            {stats.expiring_licenses?.length === 0 ? (
              <div className="text-sm text-slate-500 py-4 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> All operator licenses are valid for the next 30 days.</div>
            ) : (
              <ScrollArea className="max-h-56">
                <table className="w-full text-xs">
                  <thead className="text-left text-slate-500 border-b sticky top-0 bg-white">
                    <tr><th className="py-2 pr-2">Operator</th><th className="py-2 pr-2">License</th><th className="py-2 pr-2">Expires</th></tr>
                  </thead>
                  <tbody className="divide-y">
                    {(stats.expiring_licenses || []).slice(0, 20).map((d) => (
                      <tr key={`lic-${d.license_no || d.full_name}-${d.expires}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate('/esd/operators')}>
                        <td className="py-1.5 pr-2 font-medium">{d.full_name}</td>
                        <td className="py-1.5 pr-2 font-mono text-[10px]">{d.license_no}</td>
                        <td className="py-1.5 pr-2">
                          {d.expired ? <Badge className="bg-red-100 text-red-800 hover:bg-red-100 text-[10px]">Expired {formatDate(d.expires)}</Badge> : <span className="text-amber-700">{formatDate(d.expires)}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent activity feed (transparency) */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><Activity className="h-5 w-5" /> Recent Activity {isSuperAdmin && <Badge variant="secondary" className="text-[10px]">Org-wide</Badge>}</CardTitle>
          {isSuperAdmin && <Button variant="ghost" size="sm" onClick={() => navigate('/esd/settings/audit-logs')}>Full audit trail <ArrowRight className="h-3 w-3 ml-1" /></Button>}
        </CardHeader>
        <CardContent>
          {(stats.recent_activity || []).length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No recent activity to display.</div>
          ) : (
            <ul className="divide-y">
              {(stats.recent_activity || []).slice(0, 10).map((a) => (
                <li key={a.id} className="py-2.5 flex items-center gap-3">
                  <div className="h-7 w-7 rounded-full bg-blue-100 text-[hsl(217,91%,24%)] grid place-items-center text-[10px] font-bold shrink-0">
                    {(a.username || '—').split('.').map((s) => s[0]).slice(0, 2).join('').toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      <span className="font-semibold text-slate-900">{a.username}</span>
                      <span className="text-slate-500"> {ACTION_LABELS[a.action] || a.action.replace(/_/g, ' ')}</span>
                      {a.entity && a.entity !== 'system' && <span className="text-slate-400 text-xs"> — {a.entity}</span>}
                    </div>
                    <div className="text-[11px] text-slate-400">{formatDateTime(a.timestamp)}</div>
                  </div>
                  <Badge variant="secondary" className="text-[10px]">{ROLE_LABELS[a.role] || a.role}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Recent requests */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2"><Clock className="h-5 w-5" /> Recent Equipment Requests</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate('/esd/equipment-requests')}>View all <ArrowRight className="h-3 w-3 ml-1" /></Button>
        </CardHeader>
        <CardContent>
          {(stats.recent_requests || []).length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center flex flex-col items-center gap-2">
              <AlertCircle className="h-6 w-6 text-slate-300" /> No recent equipment requests.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-slate-500 border-b">
                  <tr><th className="py-2 pr-4">Ref #</th><th className="py-2 pr-4">Project</th><th className="py-2 pr-4">Equipment</th><th className="py-2 pr-4">Date Needed</th><th className="py-2 pr-4">Status</th></tr>
                </thead>
                <tbody className="divide-y">
                  {stats.recent_requests.map((r) => (
                    <tr key={r.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate('/esd/equipment-requests')}>
                      <td className="py-2 pr-4 font-mono text-xs">{r.reference_number}</td>
                      <td className="py-2 pr-4 font-medium">{r.project_name}</td>
                      <td className="py-2 pr-4">{r.requested_category} <span className="text-slate-400">— {r.quantity}</span></td>
                      <td className="py-2 pr-4">{formatDate(r.date_needed)}</td>
                      <td className="py-2 pr-4"><StatusBadge status={r.status} type="request" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
